import { Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, SilentRequest } from '@azure/msal-browser';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import User from '../../../core/data/dtos/user.dto';
import CurrentBankUserDto from '../../../core/data/dtos/current-bank-user.dto';
import { SwalService } from '../../../common/services/swal.service';

@Injectable()
export class AuthService {
  /** Policies are emitted here by retailers and managers only. */
  private static readonly emitterRoles: ReadonlySet<string> = new Set(['retailer', 'manager']);
  account?: AccountInfo;
  private $logout = new Subject<void>();
  _logout = this.$logout.asObservable();
  private $login = new Subject<void>();
  _login = this.$login.asObservable();
  private authTokenSubject = new BehaviorSubject<string | null>(null);
  authToken$ = this.authTokenSubject.asObservable();

  private bankProfileSubject = new BehaviorSubject<CurrentBankUserDto | null>(null);
  bankProfile$ = this.bankProfileSubject.asObservable();

  get bankProfile() {
    return this.bankProfileSubject.value;
  }

  private resolveReady!: () => void;
  /** Resolves once redirect handling, token acquisition and profile fetch have settled. */
  readonly ready: Promise<void> = new Promise((resolve) => (this.resolveReady = resolve));

  get authToken() {
    return this.authTokenSubject.value;
  }

  constructor(
    private router: Router,
    protected httpClient: HttpClient,
    private msalService: MsalService,
    private swal: SwalService,
  ) {
    // Local development: impersonate a seeded bank user, skipping B2C entirely
    if (environment.devAuth?.enabled) {
      // Still initialise MSAL: nothing signs in, but stray calls such as the header
      // logout would otherwise throw uninitialized_public_client_application.
      this.msalService.initialize().subscribe({ error: () => { } });
      this.setAuthToken('dev');

      // Stand in for the MSAL account: the header reads `user`, and isClient
      // dereferences it without a null check.
      const devUser = localStorage.getItem('devUser') || environment.devAuth.defaultUser;
      this.user = {
        localAccountId: devUser,
        username: devUser,
        name: devUser,
        idTokenClaims: { given_name: devUser },
      } as unknown as User;

      this.logIdentity('startup');

      // Deferred: calling it here would build the HTTP interceptor chain while this
      // service is still constructing, and ErrorInterceptor injects AuthService (NG0200).
      queueMicrotask(() =>
        this.getBankProfile()
          .then(() => {
            const profile = this.bankProfile;
            if (!profile) return;
            this.user = {
              ...this.user,
              name: `${profile.name} ${profile.lastName}`.trim(),
              idTokenClaims: { ...this.user.idTokenClaims, given_name: profile.name },
            } as unknown as User;
          })
          .finally(() => this.resolveReady()),
      );
      return;
    }

    // `ready` gates the quote flow, so every path here must resolve it - including
    // failures. Leaving it pending would hang the page for anonymous customers too.
    this.msalService.initialize().subscribe({
      next: () => {
        this.msalService.handleRedirectObservable().subscribe({
          next: async (result) => {
            try {
              if (result?.account) {
                this.msalService.instance.setActiveAccount(result.account);
              }
              this.getAccount();
              await this.getToken();
            } finally {
              this.resolveReady();
            }
          },
          error: (error) => {
            console.error('Failed to process login redirect', error);
            this.resolveReady();
            this.router.navigate(['/auth/login-failed']);
          },
        });
      },
      error: (error) => {
        console.error('Failed to initialize authentication', error);
        this.resolveReady();
      },
    });
  }

  private _user!: User;

  userSubject = new BehaviorSubject<User>(this._user);

  get user() {
    return this._user;
  }

  set user(user: User) {
    this._user = user;

    this.userSubject.next(user);
  }

  get isClient() {
    return this.user.idTokenClaims?.extension_IdNumber;
  }

  get isIntermediary() {
    return this.user.idTokenClaims?.extension_ProducerNumber;
  }

  /** Role name from the token, lowercased. B2C emits extension_EmployeeRole ("Manager"). */
  private get tokenRole(): string {
    const claims = this.user?.idTokenClaims as any;
    const raw = claims?.extension_EmployeeRole ?? claims?.extension_UserType ?? '';
    return String(raw).toLowerCase();
  }

  /**
   * Check if user is a Supervisor based on the resolved bank profile, with token claims as fallback
   */
  get isSupervisor(): boolean {
    if (this.bankProfile) return this.bankProfile.role === 'supervisor';
    const claims = this.user?.idTokenClaims as any;
    return this.tokenRole === 'supervisor' || claims?.extension_IsSupervisor === true;
  }

  /**
   * Check if user is a Retailer (Representante) based on the resolved bank profile, with token claims as fallback
   */
  get isRetailer(): boolean {
    if (this.bankProfile) return this.bankProfile.role === 'retailer';
    const claims = this.user?.idTokenClaims as any;
    return this.tokenRole === 'retailer' || claims?.extension_IsRetailer === true;
  }

  /**
   * Check if user is a Manager based on the resolved bank profile, with token claims as fallback
   */
  get isManager(): boolean {
    if (this.bankProfile) return this.bankProfile.role === 'manager';
    const claims = this.user?.idTokenClaims as any;
    return this.tokenRole === 'manager' || claims?.extension_IsManager === true;
  }

  /**
   * Get user's BankId from the resolved bank profile, with token claims as fallback
   */
  get bankId(): number | null {
    if (this.bankProfile?.bankId != null) return this.bankProfile.bankId;
    // Supervisors carry no single bank; send one only when it is unambiguous. The API
    // scopes by the token regardless, so sending nothing is still correct.
    const bankIds = this.bankProfile?.bankIds ?? [];
    if (bankIds.length === 1) return bankIds[0];
    const claims = this.user?.idTokenClaims as any;
    return claims?.extension_BankId ? Number(claims.extension_BankId) : null;
  }

  /**
   * Get user's branch from the resolved bank profile
   */
  get branchId(): number | null {
    if (this.bankProfile?.branchId != null) return this.bankProfile.branchId;
    const branchIds = this.bankProfile?.branchIds ?? [];
    return branchIds.length === 1 ? branchIds[0] : null;
  }

  /**
   * Get the internal user ID (BankEmployee/Supervisor id) for quote attribution
   */
  get userId(): number | null {
    if (this.bankProfile) {
      return this.bankProfile.employeeId ?? this.bankProfile.supervisorId ?? null;
    }
    const claims = this.user?.idTokenClaims as any;
    return claims?.extension_UserId ? Number(claims.extension_UserId) : null;
  }

  get home() {
    return '';
  }

  setAuthToken(token: string | null) {
    this.authTokenSubject.next(token);
  }

  async logOut() {
    this.msalService.logout();
    this.user = undefined as any;
    this.bankProfileSubject.next(null);
    this.$logout.next();
  }

  private getAccount() {
    if (this.msalService.instance.getAllAccounts().length == 0) return;

    this.account = this.msalService.instance.getAllAccounts()[0];

    this.user = this.account as User;
    this.logIdentity('account resolved');
  }

  /**
   * Development aid: prints who the app thinks you are. `objectId (oid)` is the value
   * that has to match AzureId on the SuperUsers / Supervisors / BankEmployees row.
   * Silent in production builds.
   */
  logIdentity(stage = 'profile loaded'): void {
    if (!isDevMode()) return;

    // Re-inspect at any time from the console with: authUser()
    (window as any).authUser ??= () => this.logIdentity('on demand');

    const claims = (this.account?.idTokenClaims ?? {}) as Record<string, any>;
    const profile = this.bankProfile;
    const source = environment.devAuth?.enabled ? 'dev impersonation' : 'Azure B2C';
    const oid =
      this.account?.localAccountId ??
      claims['oid'] ??
      (environment.devAuth?.enabled
        ? localStorage.getItem('devUser') || environment.devAuth.defaultUser
        : null);

    console.log(
      `%c[auth]%c ${source} · ${stage} · ${profile ? `${profile.name} ${profile.lastName} (${profile.role})` : oid ?? 'anonymous'}`,
      'background:#9A0113;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
      'color:inherit',
      {
        objectId: oid,
        username: this.account?.username ?? null,
        policy: claims['tfp'] ?? claims['acr'] ?? null,
        bankProfile: profile,
        roles: {
          supervisor: this.isSupervisor,
          manager: this.isManager,
          retailer: this.isRetailer,
          client: !!this.isClient,
        },
        account: this.account ?? null,
        idTokenClaims: claims,
      },
    );
  }

  private async getToken() {
    if (!this.msalService || !this.account) return;

    const tokenRequest: SilentRequest = {
      account: this.account,
      scopes: environment.apiConfig.scopes,
    };

    try {
      const response =
        await this.msalService.instance.acquireTokenSilent(tokenRequest);
      this.user = {
        ...this.user,
        accessToken: response.accessToken,
        expiresOn: response.expiresOn,
      };
      this.setAuthToken(response.accessToken);
      if (this.isClient) {
        this.getMe();
      } else {
        await this.getBankProfile();
      }
    } catch (e) {
      // Don't force a logout: the public quote flow must keep working, and a bank user
      // with an unrenewable cached account would otherwise be bounced out with no message.
      console.error('Failed to get access token silently', e);
      this.setAuthToken(null);
    }
  }

  private async getBankProfile() {
    try {
      const profile = await lastValueFrom(
        this.httpClient.get<CurrentBankUserDto>('Employees/me'),
      );

      if (profile && !AuthService.emitterRoles.has(profile.role)) {
        await this.rejectNonEmitterSession();
        return;
      }

      this.bankProfileSubject.next(profile);
    } catch (e: any) {
      this.bankProfileSubject.next(null);
      if (e?.status !== 404) {
        console.error('Failed to load bank user profile', e);
      }
    } finally {
      this.logIdentity();
    }
  }

  /**
   * Supervisors and administrators work in the backoffice; letting them stay
   * signed in here would attribute quotes they cannot emit. Explains why and
   * ends the session.
   */
  private async rejectNonEmitterSession() {
    this.bankProfileSubject.next(null);
    this.setAuthToken(null);

    await this.swal.error(
      'Esta aplicación es para representantes y gerentes de banco. Para gestionar tu cuenta, ingresa al backoffice.',
      'info',
    );

    if (environment.devAuth?.enabled) {
      localStorage.removeItem('devUser');
      location.assign('/');
      return;
    }

    const account = this.account;
    this.user = undefined as any;
    this.account = undefined;
    this.$logout.next();

    if (account) {
      // Ends the B2C cookie session too; otherwise the next "iniciar sesión"
      // silently signs the same account back in.
      this.msalService
        .logoutRedirect({ account, postLogoutRedirectUri: '/' })
        .subscribe({
          error: (error) =>
            console.error('Failed to sign out of the identity provider', error),
        });
    }
  }

  private async getMe() {
    const result: any = await lastValueFrom(
      this.httpClient.get(
        'clients?idNumber=' + this.user.idTokenClaims?.extension_IdNumber,
      ),
    );
    this.user = {
      ...this.user,
      phone: result.data.phone.replace(/\D/g, ''),
    };
  }

  async refreshToken(): Promise<string | null> {
    if (!this.account) return null;

    const tokenRequest: SilentRequest = {
      account: this.account,
      scopes: environment.apiConfig.scopes,
    };

    try {
      const response =
        await this.msalService.instance.acquireTokenSilent(tokenRequest);
      this.user = {
        ...this.user,
        accessToken: response.accessToken,
        expiresOn: response.expiresOn,
      };
      this.setAuthToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      console.error('Error trying to refresh token silently', error);
      return null;
    }
  }
}
